import moment from 'moment';
import { OptionFormTypes } from './filter-form-render-service/option-form-types';
import { orderFilterOptions } from '../../containers/orders-filter-bar-container/filter-options/options/';
import ProductsFilterOptions from '../../pages/products-page/filter-options';
import ShipmentsFilterOptions from '../../pages/shipping-page/shipping-filter-bar-container/filter-options/options/index';
import { dateFilterOperators } from '../../containers/base-filter-bar-container/reference-data-criteria-arguments';
import { criteriaForCollectionFilterType, reduceCollectionFilterTypeIfExists } from './collection-filter-mapping-service';
import { BillToParty, ACCOUNT } from '../../enums/bill-to-party';
import { SHIPPING_ACCOUNT } from '../../filters/constants';
import { id as weightFilterId } from '../../filters/options/weight';
import { WeightUnit } from '../../enums/weight-unit';
import { MM_DD_YYYY } from '../../constants/date-format';
import { convertDateToMomentDateObjAtMidnightInSellerTimeZone } from '../datetime-service';

const dateFormat = MM_DD_YYYY;
const ORDERS = 'orders';
const PRODUCTS = 'products';
const SHIPMENTS = 'shipments';
const today = moment().format(dateFormat);
const tomorrow = moment().add(1, 'days').format(dateFormat);

export const calculateTargetDatesFromOperator = (dateOperator, gridType, sellerTimeZone) => {
  // Because of discrepancies in how the BE handles datetime and timezones with regards to
  // ORDERS, SHIPMENTS, and PRODUCTS grid filters we chose to handle the FE piecemeal.
  // This is to avoid blowing the scope of SPD-1568.
  if (gridType === ORDERS) {
    const orderToday = convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone);
    const orderTomorrow = convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).add(1, 'days');
    switch (dateOperator) {
      case dateFilterOperators.dateYesterday:
        return {
          lessThanDate: orderToday,
          greaterThanOrEqualToDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).subtract(1, 'days'),
        };

      case dateFilterOperators.dateLastWeek:
        return {
          lessThanDate: orderTomorrow,
          greaterThanOrEqualToDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).subtract(7, 'days'),
        };
      case dateFilterOperators.dateLast30Days:
        return {
          lessThanDate: orderTomorrow,
          greaterThanOrEqualToDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).subtract(30, 'days'),
        };
      case dateFilterOperators.dateThisMonth:
        return {
          lessThanDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).endOf('month'),
          greaterThanOrEqualToDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).startOf('month'),
        };
      case dateFilterOperators.dateLastMonth:
        return {
          lessThanDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).subtract('month', 1).endOf('month'),
          greaterThanOrEqualToDate: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone)
            .subtract('month', 1)
            .startOf('month'),
        };
      case dateFilterOperators.dateToday:
      default:
        return {
          lessThanDate: orderTomorrow,
          greaterThanOrEqualToDate: orderToday,
        };
    }
  }

  switch (dateOperator) {
    case dateFilterOperators.dateYesterday:
      return {
        lessThanDate: today,
        greaterThanOrEqualToDate: moment().subtract(1, 'days').format(dateFormat),
      };
    case dateFilterOperators.dateLastWeek:
      return {
        lessThanDate: tomorrow,
        greaterThanOrEqualToDate: moment().subtract(7, 'days').format(dateFormat),
      };
    case dateFilterOperators.dateLast30Days:
      return {
        lessThanDate: tomorrow,
        greaterThanOrEqualToDate: moment().subtract(30, 'days').format(dateFormat),
      };
    case dateFilterOperators.dateThisMonth:
      return {
        lessThanDate: moment().endOf('month').startOf('day').add(1, 'days').format(dateFormat),
        greaterThanOrEqualToDate: moment().startOf('month').startOf('day').format(dateFormat),
      };
    case dateFilterOperators.dateLastMonth:
      return {
        lessThanDate: moment().subtract('month', 1).endOf('month').startOf('day').add(1, 'days').format(dateFormat),
        greaterThanOrEqualToDate: moment().subtract('month', 1).startOf('month').startOf('day').format(dateFormat),
      };
    case dateFilterOperators.dateToday:
    default:
      return {
        lessThanDate: tomorrow,
        greaterThanOrEqualToDate: today,
      };
  }
};

export const mapCriterionToApiParam = (filter, gridType, sellerTimeZone) => {
  const { type, filterValue, filterById, criterionOperatorValue, criterionArgumentValue, criterionMeta } = filter;
  if (filterById === 'label') {
    const isCreatedOption = criterionArgumentValue.toLowerCase().includes('created');
    const paramKey = isCreatedOption ? 'isLabelCreated' : 'isLabelPrinted';
    const paramValue = !criterionArgumentValue.includes('not');
    return {
      [paramKey]: paramValue,
    };
  }
  switch (type) {
    case OptionFormTypes.TYPE_IS_OPTIONS:
    case OptionFormTypes.TYPE_CHECKLIST:
      return {
        [filterValue]: criterionArgumentValue,
      };
    case OptionFormTypes.TYPE_CONTAINS: {
      const paramKey = `${filterValue}${criterionOperatorValue}`;
      return {
        [paramKey]: criterionArgumentValue,
      };
    }
    case OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER:
    case OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER_OR_BLANK: {
      let filterReturn;
      // orders grid uses criterionMeta, shipping grid does not
      if (filterById === SHIPPING_ACCOUNT && criterionMeta && ACCOUNT !== criterionMeta.value) {
        filterReturn = {
          ...filter,
          filterValue: 'BillToParty',
          criterionArgumentValue: [BillToParty.indexOf(criterionMeta.value)],
        };
      }
      return criteriaForCollectionFilterType(filterReturn || filter);
    }
    case OptionFormTypes.TYPE_IS_OR_ISNOT_MULTI_OPTIONS: {
      const operatorIsEquality = criterionOperatorValue === 'equals';
      if (operatorIsEquality) {
        return {
          [`${filterValue}`]: criterionArgumentValue,
        };
      }
      // TODO - Remove placeholder - the (X IS NOT Y) contract has not been defined yet.
      return {
        [`${filterValue}isNot`]: criterionArgumentValue,
      };
    }
    case OptionFormTypes.TYPE_NUMBER_COMPARISON:
    case OptionFormTypes.TYPE_CURRENCY_COMPARISON: {
      const values = Array.isArray(criterionArgumentValue) ? criterionArgumentValue : [criterionArgumentValue];
      return {
        numericFilters: {
          column: filterValue,
          operator: criterionOperatorValue,
          values,
          ...(filterById === weightFilterId && {
            unitOfMeasure: criterionMeta?.unit ?? WeightUnit.Ounces,
          }),
        },
      };
    }
    case OptionFormTypes.TYPE_ORDER_DATE_COMPARISON:
    case OptionFormTypes.TYPE_DATE_COMPARISON_SHIPMENTS:
    case OptionFormTypes.TYPE_DATE_COMPARISON: {
      const dateOperatorType = criterionOperatorValue;
      const selectedDateOrNumberOfDays = criterionArgumentValue;

      if (gridType === ORDERS) {
        if (dateOperatorType === dateFilterOperators.dateOnOrBefore) {
          return {
            dateTimeFilters: {
              column: filterValue,
              operator: 'Lt',
              value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(selectedDateOrNumberOfDays), sellerTimeZone).add(
                1,
                'days',
              ),
            },
          };
        }
        if (dateOperatorType === dateFilterOperators.dateOnOrAfter) {
          return {
            dateTimeFilters: {
              column: filterValue,
              operator: 'Gte',
              value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(selectedDateOrNumberOfDays), sellerTimeZone),
            },
          };
        }
        if (dateOperatorType === dateFilterOperators.dateCustomRange) {
          return {
            dateTimeFilters: [
              {
                column: filterValue,
                operator: 'Gte',
                value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(criterionArgumentValue.startRangeDate), sellerTimeZone),
              },
              {
                column: filterValue,
                operator: 'Lt',
                value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(
                  moment(criterionArgumentValue.endRangeDate),
                  sellerTimeZone,
                ).add(1, 'days'),
              },
            ],
          };
        }
        if (dateOperatorType === dateFilterOperators.dateWithinThePastXDays) {
          return {
            dateTimeFilters: [
              {
                column: filterValue,
                operator: 'Gte',
                value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(
                  moment().subtract(Number(selectedDateOrNumberOfDays), 'days'),
                  sellerTimeZone,
                ),
              },
              {
                column: filterValue,
                operator: 'Lt',
                value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone),
              },
            ],
          };
        }
        if (dateOperatorType === dateFilterOperators.dateWithinTheNextXDays) {
          return {
            dateTimeFilters: [
              {
                column: filterValue,
                operator: 'Gte',
                value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).endOf('day'),
              },
              {
                column: filterValue,
                operator: 'Lt',
                value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(
                  moment().add(Number(selectedDateOrNumberOfDays), 'days'),
                  sellerTimeZone,
                ),
              },
            ],
          };
        }
        if (dateOperatorType === dateFilterOperators.dateXDaysAgoOrMore) {
          return {
            dateTimeFilters: {
              column: filterValue,
              operator: 'Lt',
              value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(
                moment().subtract(Number(selectedDateOrNumberOfDays) - 1, 'days'),
                sellerTimeZone,
              ),
            },
          };
        }
        if (dateOperatorType === dateFilterOperators.dateXDaysAheadOrMore) {
          return {
            dateTimeFilters: {
              column: filterValue,
              operator: 'Gte',
              value: convertDateToMomentDateObjAtMidnightInSellerTimeZone(
                moment().add(Number(selectedDateOrNumberOfDays), 'days'),
                sellerTimeZone,
              ),
            },
          };
        }
        const targetDates = calculateTargetDatesFromOperator(dateOperatorType, gridType, sellerTimeZone);
        return {
          dateTimeFilters: [
            {
              column: filterValue,
              operator: 'Gte',
              value: targetDates.greaterThanOrEqualToDate,
            },
            {
              column: filterValue,
              operator: 'Lt',
              value: targetDates.lessThanDate,
            },
          ],
        };
      }

      if (dateOperatorType === dateFilterOperators.dateOnOrBefore) {
        return {
          dateTimeFilters: {
            column: filterValue,
            operator: 'Lt',
            value: moment(selectedDateOrNumberOfDays).add(1, 'days').format(dateFormat),
          },
        };
      }
      if (dateOperatorType === dateFilterOperators.dateOnOrAfter) {
        return {
          dateTimeFilters: {
            column: filterValue,
            operator: 'Gte',
            value: moment(selectedDateOrNumberOfDays).format(dateFormat),
          },
        };
      }
      if (dateOperatorType === dateFilterOperators.dateCustomRange) {
        return {
          dateTimeFilters: [
            {
              column: filterValue,
              operator: 'Gte',
              value: moment(criterionArgumentValue.startRangeDate).format(dateFormat),
            },
            {
              column: filterValue,
              operator: 'Lt',
              value: moment(criterionArgumentValue.endRangeDate).add(1, 'days').format(dateFormat),
            },
          ],
        };
      }
      if (dateOperatorType === dateFilterOperators.dateWithinThePastXDays) {
        return {
          dateTimeFilters: [
            {
              column: filterValue,
              operator: 'Gte',
              value: moment().subtract(Number(selectedDateOrNumberOfDays), 'days').format(dateFormat),
            },
            {
              column: filterValue,
              operator: 'Lt',
              value: moment().add(1, 'days').format(dateFormat),
            },
          ],
        };
      }
      if (dateOperatorType === dateFilterOperators.dateWithinTheNextXDays) {
        return {
          dateTimeFilters: [
            {
              column: filterValue,
              operator: 'Gte',
              value: moment().format(dateFormat),
            },
            {
              column: filterValue,
              operator: 'Lt',
              value: moment()
                .add(Number(selectedDateOrNumberOfDays) + 1, 'days')
                .format(dateFormat),
            },
          ],
        };
      }
      if (dateOperatorType === dateFilterOperators.dateXDaysAgoOrMore) {
        return {
          dateTimeFilters: {
            column: filterValue,
            operator: 'Lt',
            value: moment()
              .subtract(Number(selectedDateOrNumberOfDays) - 1, 'days')
              .format(dateFormat),
          },
        };
      }
      if (dateOperatorType === dateFilterOperators.dateXDaysAheadOrMore) {
        return {
          dateTimeFilters: {
            column: filterValue,
            operator: 'Gte',
            value: moment().add(Number(selectedDateOrNumberOfDays), 'days').format(dateFormat),
          },
        };
      }
      const targetDates = calculateTargetDatesFromOperator(dateOperatorType, gridType, sellerTimeZone);
      return {
        dateTimeFilters: [
          {
            column: filterValue,
            operator: 'Gte',
            value: targetDates.greaterThanOrEqualToDate,
          },
          {
            column: filterValue,
            operator: 'Lt',
            value: targetDates.lessThanDate,
          },
        ],
      };
    }
    case OptionFormTypes.TYPE_DATE_COMPARISON_DEPRECATED: {
      // TODO: Remove when no longer in use per SPD-1623 - Greg S 1/10/20
      const dateOperatorType = criterionOperatorValue;
      const selectedDate = criterionArgumentValue;
      if (dateOperatorType === dateFilterOperators.dateCustomRange) {
        const { endRangeDate, startRangeDate } = selectedDate;
        return {
          [`${filterValue}Lt`]: moment(endRangeDate).add(1, 'days').format(dateFormat),
          [`${filterValue}Gte`]: moment(startRangeDate).format(dateFormat),
        };
      }
      if (dateOperatorType === dateFilterOperators.dateOnOrBefore) {
        return {
          [`${filterValue}Lt`]: moment(selectedDate).add(1, 'days').format(dateFormat),
        };
      }
      if (dateOperatorType === dateFilterOperators.dateOnOrAfter) {
        return {
          [`${filterValue}Gte`]: moment(selectedDate).format(dateFormat),
        };
      }
      const targetDates = calculateTargetDatesFromOperator(dateOperatorType);
      return {
        [`${filterValue}Gte`]: targetDates.greaterThanOrEqualToDate,
        [`${filterValue}Lt`]: targetDates.lessThanDate,
      };
    }
    case OptionFormTypes.TYPE_STRING: {
      const values = Array.isArray(criterionArgumentValue) ? criterionArgumentValue : [criterionArgumentValue];
      return {
        stringFilters: {
          column: filterValue,
          operator: criterionOperatorValue,
          values,
        },
      };
    }
    case OptionFormTypes.TYPE_BOOLEAN: {
      return {
        booleanFilters: {
          column: filterValue,
          value: criterionArgumentValue,
        },
      };
    }
    case OptionFormTypes.TYPE_IS_FREE_INPUT_DEPRECATED: // TODO: Remove when no longer in use per SPD-1566 - Greg S 1/7/20
    default: {
      return {
        [`${filterValue}`]: criterionArgumentValue,
      };
    }
  }
};

export const generateMappedKeyValue = (filtersRequest) => {
  const { body, mappedKey, mappedValue, mappedFilter, baseFilter } = filtersRequest;
  if (body[mappedKey]) {
    if (Array.isArray(mappedFilter[mappedKey])) {
      return {
        [mappedKey]: [...body[mappedKey], ...mappedFilter[mappedKey]],
      };
    }
    return {
      [mappedKey]: [...body[mappedKey], mappedFilter[mappedKey]],
    };
  }
  if (mappedValue !== null && typeof mappedValue === 'object') {
    return {
      ...body,
      [mappedKey]: Array.isArray(mappedValue) ? mappedValue : [mappedValue],
    };
  }
  return {
    ...body,
    [mappedKey]: baseFilter && baseFilter.expectsArgumentAsArray ? [mappedValue] : mappedValue,
  };
};

const gridTypeToFilterOptions = {
  [ORDERS]: orderFilterOptions,
  [PRODUCTS]: ProductsFilterOptions,
  [SHIPMENTS]: ShipmentsFilterOptions,
};

const NeverModifiedFilter = {
  id: 'neverModified',
  type: OptionFormTypes.TYPE_BOOLEAN,
  filterValue: 'NeverModified',
  criterionArgumentValue: true,
};

// TODO: This is a temp patch, eventually, I want all baseFilters defining their own buildApiRequest() implementation
export const mapCriteriaToRequest = (filters, gridType, sellerTimeZone) => {
  const filterOptions = gridTypeToFilterOptions[gridType];
  const filtersToOperateOn = reduceCollectionFilterTypeIfExists(filters);

  const requestBody = filtersToOperateOn.reduce((body, filter) => {
    let baseFilter = filterOptions.find((option) => option.id === filter.filterById);
    if (filter.criterionOperatorValue === 'neverModified') {
      baseFilter = NeverModifiedFilter;
      Object.assign(filter, baseFilter);
    }
    const mappedFilter = mapCriterionToApiParam(filter, gridType, sellerTimeZone);
    const [mappedKey] = Object.keys(mappedFilter);
    const mappedValue = mappedFilter[mappedKey];
    return {
      ...body,
      ...generateMappedKeyValue({
        body,
        mappedKey,
        mappedValue,
        mappedFilter,
        baseFilter,
      }),
    };
  }, {});
  return requestBody;
};

export const mapOrdersFiltersToRequestBody = (filters, sellerTimeZone) => {
  return mapCriteriaToRequest(filters, ORDERS, sellerTimeZone);
};

export const mapProductsFiltersToRequestBody = (filters) => {
  return mapCriteriaToRequest(filters, PRODUCTS);
};

export const mapShipmentsFiltersToRequestBody = (filters) => {
  return mapCriteriaToRequest(filters, SHIPMENTS);
};
